// MWA-420 — reading a company's tier off the client's Dream Job list.
//
// Mirrors deriveCompanyTier in backend/layers/helpers/nodejs/jobOpportunityRules.mjs
// for the Side Door tabs, which group contacts by the tier of the company they
// work at. Keep the two in sync: the same company must land on the same tier
// here as it does on an application's companyTier.
import type { TargetCompanyTier } from '../services/api'
import { normalizeCompanyName } from './companyName'

export const TARGET_COMPANY_TIERS: readonly TargetCompanyTier[] = ['dream', 'target', 'backup', 'wildcard']

/**
 * A stored tier as the current vocabulary. 'goal' folded Dream and Target
 * together and can still be sitting in older records until the migration has
 * run; it is read as 'target'. Anything else that is not a tier is null.
 */
export function readCompanyTier(tier: unknown): TargetCompanyTier | null {
  if (tier === 'goal') return 'target'
  return TARGET_COMPANY_TIERS.includes(tier as TargetCompanyTier) ? (tier as TargetCompanyTier) : null
}

/**
 * Normalized company name to tier, for every company on the client's list.
 * A company that is listed but has no usable tier (a legacy bare string, or a
 * stored value outside the vocabulary) is 'wildcard': the client named it, but
 * nobody ranked it. A company absent from the map is unlisted. The first entry
 * for a name wins, as it does on the server.
 */
export function buildTargetCompanyMap(targetCompanies: unknown): Record<string, TargetCompanyTier> {
  const map: Record<string, TargetCompanyTier> = {}
  if (!Array.isArray(targetCompanies)) return map

  for (const entry of targetCompanies) {
    const isObject = typeof entry === 'object' && entry !== null
    const name = typeof entry === 'string' ? entry : isObject ? (entry as { name?: unknown }).name : undefined
    const key = normalizeCompanyName(typeof name === 'string' ? name : undefined)
    if (!key || Object.prototype.hasOwnProperty.call(map, key)) continue
    map[key] = isObject ? (readCompanyTier((entry as { tier?: unknown }).tier) ?? 'wildcard') : 'wildcard'
  }
  return map
}
